import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { DataTable } from '@/components/ui';
import {
  type RepaymentMonthly,
  type RepaymentPerLoan,
  useRepaymentTracker,
} from '@/hooks/use-repayment-tracker';
import { cn } from '@/lib/cn';
import { fmtCurrency } from '@/lib/format';

function RepaymentMonthlyChart({ data }: { data: RepaymentMonthly[] }) {
  if (data.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-8">No repayment data available</p>;
  }

  const maxValue = Math.max(...data.flatMap(d => [d.expected, d.received]), 1);
  const isAllZeroData = data.every(
    month => month.expected === 0 && month.received === 0 && month.variance === 0,
  );

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 h-48">
        {data.map((month, i) => {
          const expectedPct = (month.expected / maxValue) * 100;
          const receivedPct = (month.received / maxValue) * 100;
          const isPositive = month.variance >= 0;

          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-2 group relative">
              {/* Tooltip on hover */}
              <div className="absolute -top-20 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                <div className="font-medium">{month.label}</div>
                <div className="text-gray-300">Expected: {fmtCurrency(month.expected)}</div>
                <div className="text-gray-300">Received: {fmtCurrency(month.received)}</div>
                <div
                  className={cn(
                    'font-medium',
                    isAllZeroData
                      ? 'text-gray-300'
                      : isPositive
                        ? 'text-green-300'
                        : 'text-red-300',
                  )}
                >
                  {isPositive ? '+' : ''}
                  {fmtCurrency(month.variance)}
                </div>
              </div>

              {/* Bars side-by-side */}
              <div className="flex gap-1 w-full items-end h-32">
                {/* Expected (gray) */}
                <div
                  className="flex-1 bg-gray-300 rounded-t-sm transition-all"
                  style={{ height: `${Math.max(expectedPct, 3)}%` }}
                  title={`Expected: ${fmtCurrency(month.expected)}`}
                />
                {/* Received (green if positive, red if under) */}
                <div
                  className={cn(
                    'flex-1 rounded-t-sm transition-all',
                    isAllZeroData
                      ? 'bg-gray-300'
                      : month.collectionRate >= 100
                        ? 'bg-green-500'
                        : 'bg-red-400',
                  )}
                  style={{ height: `${Math.max(receivedPct, 3)}%` }}
                  title={`Received: ${fmtCurrency(month.received)}`}
                />
              </div>

              {/* Collection rate */}
              <div
                className={cn(
                  'text-[10px] font-medium',
                  isAllZeroData ? 'text-gray-400' : 'text-gray-700',
                )}
              >
                {month.collectionRate.toFixed(0)}%
              </div>

              {/* Month label */}
              <span className="text-[9px] text-gray-400 truncate w-full text-center">
                {month.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs mt-4">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 bg-gray-300 rounded-sm" />
          <span className="text-gray-600">Expected</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className={cn('w-2.5 h-2.5 rounded-sm', isAllZeroData ? 'bg-gray-300' : 'bg-green-500')}
          />
          <span className="text-gray-600">Received (On Track)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className={cn('w-2.5 h-2.5 rounded-sm', isAllZeroData ? 'bg-gray-300' : 'bg-red-400')}
          />
          <span className="text-gray-600">Received (Behind)</span>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'ON_TRACK' | 'PARTIAL' | 'MISSED' | 'AHEAD' }) {
  const config = {
    ON_TRACK: { bg: 'bg-green-50', text: 'text-green-700', label: 'On Track' },
    PARTIAL: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Partial' },
    MISSED: { bg: 'bg-red-50', text: 'text-red-700', label: 'Missed' },
    AHEAD: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Ahead' },
  };

  const cfg = config[status];

  return (
    <span className={cn('px-2 py-1 rounded-full text-[10px] font-medium', cfg.bg, cfg.text)}>
      {cfg.label}
    </span>
  );
}

export function RepaymentTracker() {
  const { data, isLoading } = useRepaymentTracker(6);

  if (isLoading || !data) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Repayment Tracker</h2>
        <div className="flex items-center justify-center h-40 text-gray-400">
          <p className="text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  const { monthly, totals, perLoan } = data;
  const isAllZeroData = monthly.every(
    month => month.expected === 0 && month.received === 0 && month.variance === 0,
  );

  return (
    <div className="space-y-6">
      {/* Monthly Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900">Repayment Tracker</h2>
          <span className="text-xs text-gray-400">Last 6 months</span>
        </div>

        <RepaymentMonthlyChart data={monthly} />

        {/* Totals summary */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Total Expected</p>
            <p className="text-base font-semibold text-gray-900">
              {fmtCurrency(totals.totalExpected)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Total Received</p>
            <p className="text-base font-semibold text-gray-900">
              {fmtCurrency(totals.totalReceived)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Overall Collection Rate</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-base font-semibold text-gray-900">
                {totals.overallCollectionRate.toFixed(1)}%
              </p>
              {isAllZeroData ? (
                <Minus className="w-4 h-4 text-gray-400" />
              ) : totals.overallCollectionRate >= 100 ? (
                <TrendingUp className="w-4 h-4 text-green-600" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Per-Loan Breakdown */}
      {perLoan.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Current Month Repayments by Loan
          </h2>
          <DataTable<RepaymentPerLoan>
            columns={[
              {
                key: 'loanNumber',
                header: 'Loan',
                render: row => (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-mono font-medium text-gray-900">{row.loanNumber}</span>
                    <span className="text-xs text-gray-400">{row.borrowerName}</span>
                  </div>
                ),
              },
              {
                key: 'expectedThisMonth',
                header: 'Expected',
                align: 'right',
                render: row => (
                  <span className="text-gray-900">{fmtCurrency(row.expectedThisMonth)}</span>
                ),
              },
              {
                key: 'receivedThisMonth',
                header: 'Received',
                align: 'right',
                render: row => (
                  <span className="text-gray-900">{fmtCurrency(row.receivedThisMonth)}</span>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                align: 'right',
                render: row => <StatusBadge status={row.status} />,
              },
            ]}
            data={perLoan}
            keyField="loanId"
            emptyMessage="No loans with repayment schedules"
          />
        </div>
      )}
    </div>
  );
}
